import {
  Card,
  CardBody,
  CardHeader,
  Input,
  Button,
  Col,
  Row,
  Spinner,
  Badge,
  InputGroup
} from 'reactstrap'
import {useEffect, useState, useRef} from 'react'
import {X} from 'react-feather'
import {useAtom, useSetAtom} from 'jotai'
import {
  isMqttAtom,
  logDataAtom,
  logErrorAtom,
  logDownloadChunkAtom,
  logDownloadErrorAtom,
  type LogData
} from '@src/utility/atoms'
import Constant from '@src/utility/Constants'
import IMessage from '@src/data/messages'
import {RESET} from 'jotai/utils'

const MQTT_MAX_LINES = 500
const MQTT_DOWNLOAD_CHUNK_SIZE = 1024 * 1024

type LogDownloadState = {
  session: string
  fileName: string
  totalSize: number
  offset: number
  chunks: BlobPart[]
}

const Log = (props: any) => {
  const {SendCmd} = props

  const [isMqtt] = useAtom(isMqttAtom)
  const [logData] = useAtom(logDataAtom)
  const [logErrorMsg, setLogErrorMsg] = useAtom(logErrorAtom)
  const [logDownloadChunk] = useAtom(logDownloadChunkAtom)
  const [logDownloadErrorMsg] = useAtom(logDownloadErrorAtom)
  const resetLogDownloadChunk = useSetAtom(logDownloadChunkAtom)
  const resetLogDownloadError = useSetAtom(logDownloadErrorAtom)

  const [logLines, setLogLines] = useState<string[]>([])
  const [totalLines, setTotalLines] = useState<number>(0)
  const [fileSize, setFileSize] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [linesPerPage, setLinesPerPage] = useState<number>(100)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [searchInput, setSearchInput] = useState<string>('')
  const [isLoadingLogs, setIsLoadingLogs] = useState<boolean>(false)
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false)
  const [logError, setLogError] = useState<string>('')
  const [useTail, setUseTail] = useState<boolean>(true)
  const [downloadFileName, setDownloadFileName] = useState<string>('')
  const [isDownloading, setIsDownloading] = useState<boolean>(false)
  const [downloadProgress, setDownloadProgress] = useState<{
    received: number
    total: number
  }>({received: 0, total: 0})
  const autoRefreshInterval = useRef<NodeJS.Timeout | null>(null)
  const downloadStateRef = useRef<LogDownloadState | null>(null)

  // Fetch logs from API
  const fetchLogs = async () => {
    if (isMqtt) {
      await fetchLogsMqtt()
      return
    }

    setIsLoadingLogs(true)
    setLogError('')

    try {
      const offset = currentPage * linesPerPage
      const params = new URLSearchParams({
        lines: linesPerPage.toString(),
        offset: offset.toString(),
        tail: useTail.toString()
      })

      if (searchTerm) {
        params.append('search', searchTerm)
      }

      const response = await fetch(`/api/log?${params.toString()}`)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data: LogData = await response.json()
      setLogLines(data.lines)
      setTotalLines(data.totalLines)
      setFileSize(data.fileSize)
    } catch (error: any) {
      setLogError(error.message || 'Failed to fetch logs')
      console.error('Error fetching logs:', error)
    } finally {
      setIsLoadingLogs(false)
    }
  }

  const fetchLogsMqtt = async () => {
    if (!SendCmd) {
      setLogError('MQTT log request not available')
      setIsLoadingLogs(false)
      return
    }

    if (linesPerPage > MQTT_MAX_LINES) {
      setLinesPerPage(MQTT_MAX_LINES)
      return
    }

    setIsLoadingLogs(true)
    setLogError('')
    setLogErrorMsg('')

    const offset = currentPage * linesPerPage
    const msgPlainObj: IMessage = {
      f: Constant.F_LOG,
      a: Constant.A_GET,
      c: '',
      p: {
        take: linesPerPage,
        skip: offset,
        id: useTail ? 1 : 0,
        name: searchTerm
      }
    }

    SendCmd(msgPlainObj)
  }

  const base64ToUint8Array = (base64: string): Uint8Array => {
    const binaryString = window.atob(base64)
    const len = binaryString.length
    const bytes = new Uint8Array(len)
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes
  }

  const requestDownloadChunk = () => {
    if (!SendCmd) {
      setLogError('MQTT log download not available')
      setIsDownloading(false)
      return
    }

    const state = downloadStateRef.current
    if (!state) {
      setIsDownloading(false)
      return
    }

    const msgPlainObj: IMessage = {
      f: Constant.F_LOG,
      a: Constant.A_DOWNLOAD,
      c: '',
      file: state.fileName,
      p: {
        take: MQTT_DOWNLOAD_CHUNK_SIZE,
        skip: state.offset,
        name: state.session
      }
    }

    SendCmd(msgPlainObj)
  }

  const startMqttDownload = () => {
    if (isDownloading) {
      return
    }

    setLogError('')
    resetLogDownloadChunk(RESET)
    resetLogDownloadError(RESET)

    const fileName = downloadFileName.trim()
    downloadStateRef.current = {
      session: '',
      fileName,
      totalSize: 0,
      offset: 0,
      chunks: []
    }

    setDownloadProgress({received: 0, total: 0})
    setIsDownloading(true)
    requestDownloadChunk()
  }

  const finalizeDownload = () => {
    const state = downloadStateRef.current
    if (!state) {
      setIsDownloading(false)
      return
    }

    const blob = new Blob(state.chunks, {
      type: 'application/octet-stream'
    })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download =
      state.fileName ||
      `varbox-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.zip`
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)

    downloadStateRef.current = null
    setIsDownloading(false)
  }

  // Initial load
  useEffect(() => {
    fetchLogs()
  }, [currentPage, linesPerPage, searchTerm, useTail, isMqtt])

  // Auto-refresh setup
  useEffect(() => {
    if (autoRefresh) {
      autoRefreshInterval.current = setInterval(() => {
        fetchLogs()
      }, 30000) // Refresh every 30 seconds
    } else {
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current)
        autoRefreshInterval.current = null
      }
    }

    return () => {
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current)
      }
    }
  }, [autoRefresh, currentPage, linesPerPage, searchTerm, useTail, isMqtt])

  // Handle search with debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput)
      setCurrentPage(0) // Reset to first page on search
    }, 500)

    return () => clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    if (!isMqtt) {
      return
    }

    if (logData != null) {
      setLogLines(logData.lines || [])
      setTotalLines(logData.totalLines || 0)
      setFileSize(logData.fileSize || 0)
      setLogError('')
      setIsLoadingLogs(false)
    }
  }, [logData, isMqtt])

  useEffect(() => {
    if (!isMqtt) {
      return
    }

    if (logErrorMsg) {
      setLogError(logErrorMsg)
      setIsLoadingLogs(false)
    }
  }, [logErrorMsg, isMqtt])

  useEffect(() => {
    if (!isMqtt || !isDownloading) {
      return
    }

    if (!logDownloadChunk) {
      return
    }

    const state = downloadStateRef.current
    if (!state) {
      return
    }

    if (state.session && logDownloadChunk.session !== state.session) {
      return
    }

    if (!state.session) {
      state.session = logDownloadChunk.session
    }

    if (!state.fileName && logDownloadChunk.fileName) {
      state.fileName = logDownloadChunk.fileName
    }

    if (!state.totalSize && logDownloadChunk.totalSize) {
      state.totalSize = logDownloadChunk.totalSize
      setDownloadProgress(prev => ({
        received: prev.received,
        total: logDownloadChunk.totalSize
      }))
    }

    if (logDownloadChunk.data) {
      const bytes = base64ToUint8Array(logDownloadChunk.data)
      state.chunks.push(bytes)
      state.offset = logDownloadChunk.offset + bytes.length
      setDownloadProgress({
        received: state.offset,
        total: state.totalSize
      })
    }

    if (logDownloadChunk.done || state.offset >= state.totalSize) {
      finalizeDownload()
    } else {
      requestDownloadChunk()
    }
  }, [logDownloadChunk, isDownloading, isMqtt])

  useEffect(() => {
    if (!isMqtt || !isDownloading) {
      return
    }

    if (logDownloadErrorMsg) {
      setLogError(logDownloadErrorMsg)
      downloadStateRef.current = null
      setIsDownloading(false)
    }
  }, [logDownloadErrorMsg, isDownloading, isMqtt])

  useEffect(() => {
    if (isMqtt && linesPerPage > MQTT_MAX_LINES) {
      setLinesPerPage(MQTT_MAX_LINES)
    }
  }, [isMqtt, linesPerPage])

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`
  }

  const getLogLevelColor = (line: string): string => {
    const lowerLine = line.toLowerCase()
    if (lowerLine.includes('error')) return '#ff4d4f'
    if (lowerLine.includes('warn')) return '#faad14'
    if (lowerLine.includes('info')) return '#1890ff'
    if (lowerLine.includes('debug')) return '#52c41a'
    return '#ffffff'
  }

  const totalPages = Math.ceil(totalLines / linesPerPage)
  const downloadPercent =
    downloadProgress.total > 0
      ? Math.min(
          100,
          Math.round((downloadProgress.received / downloadProgress.total) * 100)
        )
      : 0

  const downloadLogsHttp = async () => {
    setLogError('')

    try {
      const fileParam = downloadFileName.trim()
      const url = fileParam
        ? `/api/log/download?file=${encodeURIComponent(fileParam)}`
        : '/api/log/download'
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      const contentDisposition = response.headers.get('Content-Disposition')
      const filenameMatch =
        contentDisposition && contentDisposition.match(/filename="?([^"]+)"?/)
      const filename =
        filenameMatch?.[1] ||
        (fileParam
          ? fileParam
          : `varbox-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.zip`)

      const link = document.createElement('a')
      link.href = blobUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(blobUrl)
    } catch (error: any) {
      setLogError(error.message || 'Failed to download logs')
    }
  }

  const downloadLogs = async () => {
    if (isMqtt) {
      startMqttDownload()
      return
    }

    await downloadLogsHttp()
  }

  return (
    <div>
      <div className="content-header row">
        <div className="content-header-left col-md-9 col-12 mb-2">
          <div className="row breadcrumbs-top">
            <div className="col-12">
              <h2
                className="content-header-title float-left mb-0"
                style={{borderRight: 0}}>
                Log Viewer
              </h2>
            </div>
          </div>
        </div>
      </div>

      <Row>
        <Col sm="12">
          <Card>
            <CardHeader>
              <Row className="align-items-center">
                <Col xs="12" lg="auto" className="mb-2 mb-lg-0">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Button
                      color="primary"
                      size="sm"
                      onClick={fetchLogs}
                      disabled={isLoadingLogs}
                      style={{minWidth: '85px'}}>
                      {isLoadingLogs ? <Spinner size="sm" /> : 'Refresh'}
                    </Button>
                    <Button
                      color={autoRefresh ? 'success' : 'secondary'}
                      size="sm"
                      onClick={() => setAutoRefresh(!autoRefresh)}
                      style={{minWidth: '140px'}}>
                      Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
                    </Button>
                    <Button
                      color={useTail ? 'info' : 'secondary'}
                      size="sm"
                      onClick={() => {
                        setUseTail(!useTail)
                        setCurrentPage(0)
                      }}
                      style={{minWidth: '100px'}}>
                      {useTail ? 'Tail Mode' : 'Head Mode'}
                    </Button>
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={downloadLogs}
                      disabled={isDownloading}
                      style={{minWidth: '140px'}}>
                      {isDownloading ? 'Downloading...' : 'Download Logs'}
                    </Button>
                  </div>
                </Col>
                <Col xs="12" lg="4" className="mb-2 mb-lg-0">
                  <InputGroup>
                    <Input
                      type="text"
                      placeholder="Search logs..."
                      value={searchInput}
                      onChange={e => setSearchInput(e.target.value)}
                    />
                    <Button
                      onClick={() => {
                        setSearchInput('')
                      }}>
                      <X size={14} />
                    </Button>
                  </InputGroup>
                  <InputGroup>
                    <Input
                      type="text"
                      placeholder="Download file (optional, e.g. vb.log.1.gz)"
                      value={downloadFileName}
                      onChange={e => setDownloadFileName(e.target.value)}
                      disabled={isDownloading}
                    />
                    <Button
                      onClick={() => {
                        setDownloadFileName('')
                      }}
                      disabled={isDownloading}>
                      <X size={14} />
                    </Button>
                  </InputGroup>
                </Col>
                <Col xs="12" lg="auto">
                  <div className="d-flex align-items-center gap-2">
                    <span style={{fontSize: '12px', whiteSpace: 'nowrap'}}>
                      Lines per page:
                    </span>
                    <Input
                      type="select"
                      value={linesPerPage}
                      onChange={e => {
                        setLinesPerPage(Number(e.target.value))
                        setCurrentPage(0)
                      }}
                      style={{width: '80px'}}>
                      <option value="50">50</option>
                      <option value="100">100</option>
                      <option value="500">500</option>
                      {!isMqtt && <option value="1000">1000</option>}
                    </Input>
                  </div>
                </Col>
              </Row>
              <Row className="mt-2 align-items-center">
                {/* <Col xs="12" lg="6" className="mb-2 mb-lg-0">
                 
                </Col> */}
                <Col xs="12" lg="auto">
                  {isDownloading && (
                    <span style={{fontSize: '12px', color: '#888'}}>
                      Downloading {formatFileSize(downloadProgress.received)} /{' '}
                      {formatFileSize(downloadProgress.total)} (
                      {downloadPercent}
                      %)
                    </span>
                  )}
                </Col>
              </Row>
              <Row className="mt-2">
                <Col>
                  <div style={{fontSize: '12px', color: '#888'}}>
                    <Badge color="secondary" className="me-2">
                      Total: {totalLines} lines
                    </Badge>
                    <Badge color="secondary" className="me-2">
                      Size: {formatFileSize(fileSize)}
                    </Badge>
                    {isMqtt && (
                      <Badge color="warning" className="me-2">
                        MQTT limit: max {MQTT_MAX_LINES} lines per page
                      </Badge>
                    )}
                    {searchTerm && (
                      <Badge color="info">Filtered by: "{searchTerm}"</Badge>
                    )}
                  </div>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {logError && (
                <div className="alert alert-danger" role="alert">
                  Error: {logError}
                </div>
              )}

              <div
                style={{
                  backgroundColor: '#1e1e1e',
                  color: '#d4d4d4',
                  padding: '15px',
                  borderRadius: '4px',
                  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                  fontSize: '12px',
                  maxHeight: '600px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  wordBreak: 'break-word'
                }}>
                {isLoadingLogs && logLines && logLines.length === 0 ? (
                  <div className="text-center">
                    <Spinner color="light" />
                    <p className="mt-2">Loading logs...</p>
                  </div>
                ) : logLines && logLines.length === 0 ? (
                  <div className="text-center text-muted">No logs found</div>
                ) : (
                  logLines &&
                  logLines.map((line, index) => (
                    <div
                      key={index}
                      style={{
                        borderBottom: '1px solid #333',
                        padding: '4px 0',
                        lineHeight: '1.5',
                        display: 'flex'
                      }}>
                      <span
                        style={{
                          color: '#6e7681',
                          marginRight: '10px',
                          flexShrink: 0,
                          minWidth: '50px'
                        }}>
                        {(useTail
                          ? totalLines - currentPage * linesPerPage - index
                          : currentPage * linesPerPage + index + 1
                        )
                          .toString()
                          .padStart(6, ' ')}
                      </span>
                      <span
                        style={{
                          color: getLogLevelColor(line),
                          flex: 1,
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-wrap',
                          overflowWrap: 'anywhere'
                        }}>
                        {line}
                      </span>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={() => {
                        setCurrentPage(Math.max(0, currentPage - 1))
                      }}
                      disabled={currentPage === 0 || isLoadingLogs}>
                      Previous
                    </Button>
                  </div>
                  <div>
                    Page {currentPage + 1} of {totalPages}
                  </div>
                  <div>
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={() => {
                        setCurrentPage(
                          Math.min(totalPages - 1, currentPage + 1)
                        )
                      }}
                      disabled={currentPage >= totalPages - 1 || isLoadingLogs}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default Log
